// import { datadogRum } from '@datadog/browser-rum';

import { datadogRum } from '@datadog/browser-rum';
import { DD_CONFIG } from 'constants/constants';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to manage Datadog RUM initialization and actions
 * @returns {Object} Object containing addAction and isInitialized functions
 */
const useDatadogRum = () => {
  const { unixId, roleId, email, name } = useSelector(
    (state) => state?.Auth?.userClaimObject || {}
  );

  /**
   * Initialize Datadog RUM with environment configuration
   */
  const initializeDatadog = () => {
    if (
      isUndefined(datadogRum.getInternalContext()?.application_id) &&
      process.env.REACT_APP_DD_ENABLE === 'true'
    ) {
      try {
        datadogRum.init({
          applicationId: DD_CONFIG.DD_APP_ID,
          clientToken: DD_CONFIG.DD_PUBLIC_CLIENT_ID,
          site: DD_CONFIG.DD_SITE,
          service: DD_CONFIG.DD_SERVICE,
          env: process.env.REACT_APP_ENV,
          version: DD_CONFIG.DD_VERSION,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 30,
          defaultPrivacyLevel: 'mask-user-input',
        });
      } catch (error) {
        console.error('Failed to initialize Datadog RUM:', error);
      }
    }
  };

  /**
   * Set user information in Datadog RUM
   */
  const setUserInfo = useCallback(() => {
    if (unixId && roleId && process.env.REACT_APP_DD_ENABLE === 'true') {
      try {
        datadogRum.setUser({
          id: unixId,
          roleId: roleId,
          email: email,
          name: name,
        });
      } catch (error) {
        console.error('Failed to set user info in Datadog RUM:', error);
      }
    }
  }, [roleId]);

  /**
   * Add a custom action to Datadog RUM
   * @param {string} actionName - The name of the action
   * @param {Object} actionData - Additional data for the action
   * @param {string} actionData.viewName - The view name for the action type
   * @param {Object} additionalParams - Any additional parameters
   */
  const addAction = (
    actionName,
    { viewName, ...actionData } = {},
    additionalParams = {}
  ) => {
    if (
      isUndefined(datadogRum.getInternalContext()?.application_id) ||
      process.env.REACT_APP_DD_ENABLE !== 'true'
    ) {
      console.warn(
        'DD not initialized or environment is not setup for RUM. Skipping action:',
        actionName
      );
      return;
    }

    try {
      const actionPayload = {
        timestamp: new Date().toISOString(),
        ...actionData,
        ...additionalParams,
        ...(unixId && { unixId }),
        ...(roleId && { roleId }),
      };

      datadogRum.addAction(actionName, actionPayload);
    } catch (error) {
      console.error('Failed to add Datadog RUM action:', error);
    }
  };
  /**
   * Add a standard capture click action
   * @param {Object} params - Parameters for the click action
   * @param {string} params.viewName - The view name to include in action type
   * @param {string} params.navigationSource - Source of navigation
   * @param {string} params.clickTarget - Target of the click
   * @param {string} params.page - Current page
   * @param {string} params.view - Current view
   * @param {Object} additionalData - Any additional data
   */
  const addClickAction = useCallback(
    ({
      viewName,
      navigationSource,
      clickTarget,
      page,
      view,
      ...additionalData
    } = {}) => {
      addAction(
        'CUSTOMER_VIEW',
        { viewName },
        {
          navigationSource,
          clickTarget,
          page,
          view,
          ...additionalData,
        }
      );
    },
    [addAction]
  );

  /**
   * Add a navigation action
   * @param {Object} params - Parameters for the navigation action
   * @param {string} params.viewName - The view name to include in action type
   * @param {string} params.from - Source page/view
   * @param {string} params.to - Destination page/view
   * @param {Object} additionalData - Any additional data
   */
  const addNavigationAction = useCallback(
    ({ viewName, from, to, ...additionalData } = {}) => {
      addAction(
        'NAVIGATION',
        { viewName },
        {
          from,
          to,
          ...additionalData,
        }
      );
    },
    [addAction]
  );

  /**
   * Add a search action
   * @param {Object} params - Parameters for the search action
   * @param {string} params.viewName - The view name to include in action type
   * @param {string} params.searchTerm - The search term
   * @param {string} params.searchType - Type of search
   * @param {number} params.resultCount - Number of results
   * @param {Object} additionalData - Any additional data
   */
  const addSearchAction = useCallback(
    ({
      viewName,
      searchTerm,
      searchType,
      resultCount,
      ...additionalData
    } = {}) => {
      addAction(
        'SEARCH',
        { viewName },
        {
          searchTerm,
          searchType,
          resultCount,
          ...additionalData,
        }
      );
    },
    [addAction]
  );

  // Set user info when user data is available
  useEffect(() => {
    if (!isNull(roleId) && isEmpty(unixId) === false) {
      setUserInfo();
    }
  }, [roleId]);

  /**
   * Close/end the current Datadog RUM session
   * This should be called on user logout to properly end the session
   */
  const closeSession = useCallback(() => {
    try {
      if (
        datadogRum.getInternalContext()?.application_id &&
        process.env.REACT_APP_DD_ENABLE === 'true'
      ) {
        // Add a logout action before closing the session
        addAction(
          'USER_LOGOUT',
          {},
          {
            timestamp: new Date().toISOString(),
            sessionEndReason: 'user_logout',
          }
        );

        // Stop the session
        datadogRum?.stopSession();
        console.log('Datadog RUM session closed on logout');
      }
    } catch (error) {
      console.error('Failed to close Datadog RUM session:', error);
    }
  }, [addAction]);

  return {
    addAction,
    addClickAction,
    addNavigationAction,
    addSearchAction,
    initializeDatadog,
    setUserInfo,
    closeSession,
  };
};

export default useDatadogRum;
